import React, { useState, useEffect } from "react";
import { X, Search, Check, Loader2 } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { fetchStates } from "../lib/hooks";

const VARIANT_STYLES = {
  green: {
    searchWrap: "bg-green-50 border-green-100 shadow-md shadow-green-200/80 hover:shadow-lg hover:shadow-green-300/70 focus:border-green-500",
    searchIcon: "group-focus-within:text-green-500",
    optionSelected: "bg-white border-green-500 shadow-md shadow-green-100",
    checkboxSelected: "bg-green-600 border-green-600",
    codeSelected: "bg-green-100 text-green-700",
    chip: "bg-green-600 text-white shadow-lg shadow-green-200",
    loadingWrap: "bg-green-50/30",
    spinner: "text-green-500",
    loadingText: "text-green-700/60",
  },
  amber: {
    // Tuned to match the MetadataEditor's Toggle and Save button.
    searchWrap: "bg-amber-50/30 border-amber-50 focus:border-amber-600",
    searchIcon: "group-focus-within:text-amber-600",
    optionSelected: "bg-white border-amber-600 shadow-md shadow-amber-100",
    checkboxSelected: "bg-amber-600 border-amber-600",
    codeSelected: "bg-amber-100 text-amber-700",
    chip: "bg-amber-600 text-white shadow-lg shadow-amber-200",
    loadingWrap: "bg-amber-50/30",
    spinner: "text-amber-600",
    loadingText: "text-amber-700/60",
  },
};

export const MultiStateSelector = ({ selectedStates = [], onChange, variant = "green" }) => {
  const v = VARIANT_STYLES[variant] || VARIANT_STYLES.green;
  const [states, setStates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  useEffect(() => {
    fetchStates().then((data) => {
      setStates(data);
      setLoading(false);
    });
  }, []);

  const filteredStates = states.filter(
    (s) =>
      s.name.toLowerCase().includes(search.toLowerCase()) ||
      s.code.toLowerCase().includes(search.toLowerCase())
  );

  const toggleState = (state) => {
    if (selectedStates.some((s) => s.code === state.code)) {
      onChange(selectedStates.filter((s) => s.code !== state.code));
    } else {
      onChange([...selectedStates, state]);
    }
  };

  if (loading) {
    return (
      <div className={`flex items-center gap-3 p-4 ${v.loadingWrap} rounded-2xl`}>
        <Loader2 className={`w-4 h-4 animate-spin ${v.spinner}`} />
        <p className={`text-sm font-bold uppercase tracking-widest ${v.loadingText}`}>Loading German States...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="relative group">
        <Search className={`absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 ${v.searchIcon} transition-colors`} size={18} />
        <input
          type="text"
          placeholder="Search states (e.g. Bayern, Berlin)..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className={`w-full pl-12 pr-4 py-4 border-2 rounded-2xl outline-none focus:bg-white font-semibold text-slate-700 transition-all placeholder:text-slate-400 ${v.searchWrap}`}
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-2 max-h-60 overflow-y-auto p-3 bg-slate-50 border border-slate-200 rounded-[2rem] shadow-[inset_0_1px_2px_rgba(15,23,42,0.04)] custom-scrollbar">
        {filteredStates.length === 0 ? (
          <p className="col-span-full text-sm font-bold text-slate-400 text-center py-8 uppercase tracking-tighter">No states found</p>
        ) : (
          filteredStates.map((state) => {
            const isSelected = selectedStates.some((s) => s.code === state.code);
            return (
              <motion.label
                whileHover={{ scale: 1.01 }}
                whileTap={{ scale: 0.98 }}
                key={state.code}
                className={`flex items-center justify-between px-4 py-3 rounded-xl cursor-pointer transition-all border-2
                  ${isSelected
                    ? v.optionSelected
                    : "bg-white border-slate-200 shadow-sm hover:border-slate-300 hover:shadow-md text-slate-600"}`}
              >
                <div className="flex items-center gap-3">
                  <div className={`w-5 h-5 rounded-md border-2 flex items-center justify-center transition-all
                    ${isSelected ? v.checkboxSelected : "bg-white border-slate-300"}`}>
                    {isSelected && <Check size={14} className="text-white" strokeWidth={4} />}
                  </div>
                  <span className="text-sm font-semibold tracking-tight">
                    {state.name}
                  </span>
                </div>
                <span className={`text-[10px] font-black px-2 py-0.5 rounded-md ${isSelected ? v.codeSelected : 'bg-slate-200 text-slate-500'}`}>
                  {state.code}
                </span>
                <input
                  type="checkbox"
                  className="hidden"
                  checked={isSelected}
                  onChange={() => toggleState(state)}
                />
              </motion.label>
            );
          })
        )}
      </div>

      <AnimatePresence>
        {selectedStates.length > 0 && (
          <motion.div 
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex flex-wrap gap-2 p-1"
          >
            {selectedStates.map((state) => (
              <motion.span
                layout
                initial={{ scale: 0.8 }}
                animate={{ scale: 1 }}
                exit={{ scale: 0.8, opacity: 0 }}
                key={state.code}
                className={`inline-flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-black ${v.chip}`}
              >
                {state.name}
                <button
                  type="button"
                  onClick={() => onChange(selectedStates.filter((s) => s.code !== state.code))}
                  className="p-0.5 bg-white/20 rounded-md hover:bg-white/40 transition-colors"
                >
                  <X size={12} />
                </button>
              </motion.span>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
